// 3rd-party modules
import { isMobile } from "react-device-detect";
import { Tooltip, message } from "antd";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import moment from "moment-timezone";

// project modules
import AccountBlockedSiteModal from "./AccountBlockedSiteModal";
import Checkbox, { CheckboxType } from "../../shared/inputs/checkbox";
import Button from "../../shared/button";
import ConfirmationPopup from "../../shared/popup/confirmationPopup";
import InfoPopup from "../../shared/popup/infoPopup";
import DataTable, { TableColumn } from "../../shared/list/dataTable";
import Loader from "../../shared/loader";
import Options from "../../shared/options";
import Select from "../../shared/inputs/select";
import Input from "../../shared/inputs/input";
import Switchable from "../../shared/additionalComponents/Switchable";
import TabStrip from "../../shared/tabstrip";
import Tab from "../../shared/tabstrip/tab";
import ListView from "../../shared/list/listView";
import Ripple from "../../shared/additionalComponents/Ripple";
import AccountApplicationDetails from "../applications/AccountApplicationDetails";
import { apiCall } from "../../../helpers/apiHelper";
import { convertToSnakecase } from "../../../helpers/objectHelper";
import { getBase64ImageUrl } from "../../../helpers";
import { ListViewType } from "../../shared/list/useListHook";
import { useCacheHook } from "../../../hooks/useCacheHook";

// apis
import * as AccountKiboshDeviceApi from '../../../apis/accountKiboshDeviceApi';
import * as CommonValueApi from '../../../apis/commonValueApi';
import * as DashApi from '../../../apis/dashApi';

// models
import { Account } from "../../../models/account";
import { AccountBlockedSite } from "../../../models/accountBlockedSite";
import { AccountKiboshDevice } from "../../../models/accountKiboshDevice";
import { AccountDevice } from "../../../models/accountDevice";
import { AdminFilterSet } from "../../../models/adminFilterSet";
import { ApiResponse } from "../../../models/response";
import { accountBlockedSiteViewModel } from "../../../models/types/accountBlockedSite";
import { CommonValue } from "../../../models/commonValue";
import { DataSourceRequest, FilterDescriptor, PaginationDescriptor, SortDescriptor } from "../../../models/dataSourceRequest";

type Props = {
  account?: Account;
  deviceReference?: string;
  refresh?: boolean;
  showFilters?: boolean;
  showSchedules?: boolean;
  kiboshDeviceReference?: string;
  onCancel?: () => void;
  onSave?: (account: Account) => void;
};

export default function AccountBlockedSites({ account, deviceReference, refresh, showFilters = true, showSchedules = true, kiboshDeviceReference, onCancel, onSave }: Props) {
  const columns: TableColumn<AccountBlockedSite>[] = [
    {
      title: 'Match',
      dataIndex: 'match',
      key: 'match',
      filterable: !isMobile
    },
    {
      title: '',
      key: 'action',
      width: 60,
      render: (_, record: any) => (
        <group data-wrap='no' data-gap="5">
          <Tooltip title="Edit"><div className='button micro' onClick={() => onEditClick(record)}><icon>edit_square</icon></div></Tooltip>
          <Tooltip title="Delete"><div className='button micro' onClick={() => onDeleteClick(record)}><icon>delete</icon></div></Tooltip>
        </group>
      ),
    }
  ];
  const [accountKiboshDevices, setAccountKiboshDevices] = useState<AccountKiboshDevice[]>([]);
  const [currentAccountKiboshDeviceReference, setCurrentAccountKiboshDeviceReference] = useState<string>("");
  const [blockable, setBlockable] = useState<any[]>([]);
  const [adminFilterSets, setAdminFilterSets] = useState<AdminFilterSet[]>([]);
  const [accountSetBlockedSites, setAccountSetBlockedSites] = useState<AccountBlockedSite[]>([]);
  const [cachedData, setCachedData] = useCacheHook();
  const [currentBlockableReference, setCurrentBlockableReference] = useState<string>("");
  const [currentBlockable, setCurrentBlockable] = useState<any>(null);
  const [preselectedBlockable, setPreselectedBlockable] = useState<any>(null);
  const [currentAccountBlockedSite, setCurrentAccountBlockedSite] = useState<AccountBlockedSite>(new AccountBlockedSite());
  const [isDeleteConfirmationPopupOpen, setIsDeleteConfirmationPopupOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingAccountKiboshDevices, setLoadingAccountKiboshDevices] = useState(false);
  const [loadingBlockable, setLoadingBlockable] = useState(false);
  const [loadingAdminFilterSets, setLoadingAdminFilterSets] = useState(false);
  const [loadingAccountSetBlockedSites, setLoadingAccountSetBlockedSites] = useState(false);
  const [reload, setReload] = useState<boolean>(false);
  const [regetFullData, setRegetFullData] = useState<boolean>(false);
  const [showAccountBlockedSiteModal, setShowAccountBlockedSiteModal] = useState(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [expandedItemId, setExpandedItemId] = useState<string>("");
  const [filterType, setFilterType] = useState<string>("preset")
  const [visibleAdminFilterSets, setVisibleAdminFilterSets] = useState<AdminFilterSet[]>([]);
  const [categories, setCategories] = useState<CommonValue[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [filterSetSearchText, setFilterSetSearchText] = useState<string>("")
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [blockableSelectionNeededPopupVisible, setBlockableSelectionNeededPopupVisible] = useState<boolean>(false);



  let abortController = new AbortController();

  useEffect( () => {
    setRegetFullData(true)
  }, []);


  useEffect(() => {
    if (reload) setReload(false);
  }, [reload]);

  useEffect(() => {
    if (refresh) setRegetFullData(true);
  }, [refresh]);

  useEffect(() => {
    if (deviceReference) setRegetFullData(true);
  }, [deviceReference]);

  useEffect(() => {
    if (regetFullData) {
      if (!kiboshDeviceReference) {
        getAccountKiboshDevicesAsync().catch(console.error);
      } else {
        getBlockableAsync(kiboshDeviceReference).catch(console.error);
      }

      if (showFilters || showSchedules) {
        getKiboshDeviceFilterSetsAsync().catch(console.error);
      }

      setRegetFullData(false);
    }
  }, [regetFullData]);

  useEffect(() => {

    let fs   = adminFilterSets ;
    let sc   = selectedCategory?.toString().toLocaleLowerCase()
    let fsst = filterSetSearchText?.toString().toLocaleLowerCase().replace(/^[\t ]*/, "").replace(/[\t ]*$/, "")

    if( sc != "all") {
      fs = fs.filter( f=> f.category?.toLocaleLowerCase() === sc )
    }
    if( fsst != "") {
      fs = fs.filter( f=> f.name?.toLocaleLowerCase().includes(fsst) )
    }
    setVisibleAdminFilterSets(fs)

  }, [selectedCategory,filterSetSearchText]);

  const getCategories = async (abortSignal?: AbortSignal) => {
    setLoadingCategories(true);
    const response = await apiCall(CommonValueApi.getApplicationCategories(abortSignal));
    setLoadingCategories(false);

    return response.success ? CommonValue.toArrayOfClass(response.data?.value || []) : [];
  };

  const getKiboshDeviceFilterSetsAsync = async (deviceRef?: string) => {
    Promise.all([getAccountSetBlockedSites(deviceRef, abortController.signal), getKiboshDeviceFilterSets(abortController.signal), getCategories(abortController.signal)]).then(
      (results: any) => {
        const blockedSites = results[0];
        const applications = results[1];
        const categories = results[2];

        setAccountSetBlockedSites(blockedSites);

        const returnValue = applications.map((app: any) => {
          return {
            ...app,
            isBlocked: blockedSites.filter((x: any) => !x.schedule).map((x: any) => x.match).includes(app.id),
            isScheduled: blockedSites.filter((x: any) => x.schedule?.length).map((x: any) => x.match).includes(app.id)
          }
        });

        setAdminFilterSets(returnValue);
	setVisibleAdminFilterSets(returnValue)
        setCategories(categories);
        //setSelectedCategory("all")
      }
    );
  }

  const getKiboshDeviceFilterSets = async (abortSignal?: AbortSignal) => {
    if (cachedData['KiboshDeviceFilterSets'] != null && cachedData['KiboshDeviceFilterSets'].length > 0) return cachedData['KiboshDeviceFilterSets'];

    setLoadingAdminFilterSets(true);
    const response = await apiCall(DashApi.getKiboshDeviceFilterSets(kiboshDeviceReference ? kiboshDeviceReference : currentAccountKiboshDeviceReference, abortSignal));
    setCachedData('KiboshDeviceFilterSets', AdminFilterSet.toArrayOfClass(response.data?.value || []).sort((a: AdminFilterSet, b: AdminFilterSet) => (a.displayOrder || 0) - (b.displayOrder || 0)));
    setLoadingAdminFilterSets(false);

    return response.success ? AdminFilterSet.toArrayOfClass(response.data?.value || []) : [];
  };

  const getAccountSetBlockedSitesAsync = async () => {
    setAccountSetBlockedSites(await getAccountSetBlockedSites(undefined, abortController.signal));
  }

  const getAccountSetBlockedSites = async (deviceRef?: string, abortSignal?: AbortSignal) => {
    setLoadingAccountSetBlockedSites(true);
    const response = await apiCall(DashApi.getClientDeviceFilters(kiboshDeviceReference ? kiboshDeviceReference : currentAccountKiboshDeviceReference, deviceRef || currentBlockableReference || deviceReference!, abortSignal));
    setLoadingAccountSetBlockedSites(false);

    return response.success ? AccountBlockedSite.toArrayOfClass(response.data?.value || []).filter(x => x.matchType === "set") : [];
  };





  const getBlockableAsync = async (routerRef?: string) => {
    //const allOption = new AccountDevice();
    //allOption.name = "All";
    //allOption.id = "All";
    //setBlockable([allOption, ...await getBlockable(routerRef, abortController.signal)]);

    const data = await getBlockable(routerRef, abortController.signal)
    setBlockable(data);

  }

  const getBlockable = async (routerRef?: string, abortSignal?: AbortSignal) => {
    setLoadingBlockable(true);
    const devicesResponse = await apiCall(DashApi.getClientDevices(kiboshDeviceReference || currentAccountKiboshDeviceReference || routerRef!, abortSignal));
    const profileResponse = await apiCall(DashApi.getDeviceGroups(kiboshDeviceReference || currentAccountKiboshDeviceReference || routerRef!, false, abortSignal));
    setLoadingBlockable(false);


    let sortedProfiles = []
    let sortedVpns = []
    let sortedDevices = []

    if (profileResponse.success) {
      sortedProfiles = (profileResponse.data?.value || []).map((x: any) => {
        return {
          ...x,
          sorter: x.id ? Number(x.id) : Infinity
        }
      }).sort((a: any, b: any) => a.sorter - b.sorter);

    }

    if(devicesResponse.success) {
      let unprofiledDevs = []
      let vpnDevs = []
      //let profilesWithVpns:any = {}
      let allDevs = devicesResponse.data?.value || []
      allDevs = allDevs.filter((x: any) => { return moment().add(-7, "days") < moment(x.updatedAt) || x.isVpn ; } )

      for(let i=0; i < allDevs.length; i++) {
        if( (allDevs[i]).group_id == null && (!(allDevs[i]).is_vpn)) {
          unprofiledDevs.push( allDevs[i])
        }
        if((allDevs[i]).group_id == null && (allDevs[i]).is_vpn ) {
          let v = allDevs[i]
          vpnDevs.push( v )
          //if( v.group_id != null) {
          //  profilesWithVpns[ (v.group_id + "") ] = true
          //}
        }
      }
      /*
      sortedProfiles = sortedProfiles.map( (x:any) => { 
        if(profilesWithVpns[("" + x.id)]) { x.group_with_vpns = true }
        return x
      })
      */
      sortedDevices = unprofiledDevs.sort( (a, b) => a.id.localeCompare(b.id) )
      sortedVpns = vpnDevs.sort( (a, b) => a.id.localeCompare(b.id) )

    }

    let combinedBlockable = sortedProfiles.concat(sortedVpns, sortedDevices)

    let updatedCurrentBlockableReference = currentBlockableReference;
    let updatedCurrentBlockable = currentBlockable
    let updatedPreselectedBlockable = preselectedBlockable


    if(deviceReference && preselectedBlockable && deviceReference != preselectedBlockable.id) {
      updatedCurrentBlockableReference = ""
      setCurrentBlockableReference(updatedCurrentBlockableReference)
    }
    if(deviceReference && (!updatedCurrentBlockableReference)) {
      let b = combinedBlockable?.filter( (x:any) => ( (x.id + "") == ("" + deviceReference) ) ).shift()
      
      updatedCurrentBlockable = b
      updatedPreselectedBlockable = b
      updatedCurrentBlockableReference = deviceReference

      setCurrentBlockable(updatedCurrentBlockable)
      setPreselectedBlockable(updatedPreselectedBlockable )
      setCurrentBlockableReference(updatedCurrentBlockableReference)


    }



    if( updatedPreselectedBlockable && updatedPreselectedBlockable.group_with_vpns == true ) {
      // preselected is profile with vpns in it
      // we have to diplay drop-down, but only for profile and the vpns within it
      // filter out everything else from list
      combinedBlockable = combinedBlockable.filter( (x:any) => ( 
        (""+x.id)       == (""+deviceReference) ||
        (""+x.group_id) == (""+deviceReference) 
      ))
    }

    return combinedBlockable

  };







  const getAccountKiboshDevicesAsync = async () => {
    setAccountKiboshDevices(await getAccountKiboshDevices({}, abortController.signal));
  }

  const getAccountKiboshDevices = async (request?: DataSourceRequest, abortSignal?: AbortSignal) => {
    setLoadingAccountKiboshDevices(true);
    const response = await apiCall(AccountKiboshDeviceApi.getAccountKiboshDevices(account?.accountId!, request, abortSignal));
    setLoadingAccountKiboshDevices(false);

    return response.success ? AccountKiboshDevice.toArrayOfClass(response.data?.value || []) : [];
  };

  const getPage = async (conditionList?: FilterDescriptor[], sortList?: SortDescriptor[], pagination?: PaginationDescriptor, abortSignal?: AbortSignal) => {


    if (!currentBlockableReference && !deviceReference) {
      return [];
    }

    
    const response = await apiCall(DashApi.getClientDeviceFilters(kiboshDeviceReference ? kiboshDeviceReference : currentAccountKiboshDeviceReference,  currentBlockableReference ? currentBlockableReference : deviceReference!, abortSignal));
    setTotalRecords(response.success ? response.extra?.totalCount || 0 : 0);
    setLoading(false);

    return response.success ? AccountBlockedSite.toArrayOfClass(response.data?.value || []).filter(x => x.matchType !== "set") : [];
  };

  const onAccountBlockedSiteSave = (e: Account) => {
    setReload(true);
  }

  const onAccountKiboshDeviceChange = (kiboshDeviceReference: any) => {
    setCurrentAccountKiboshDeviceReference(kiboshDeviceReference);
    getBlockableAsync(kiboshDeviceReference);
  }

  const onBlockableChange = (blockableReference: any) => {
    setCurrentBlockableReference(blockableReference);

    let b = blockable?.filter( (x:any) => ( (x.id + "") == blockableReference) ).shift()
    setCurrentBlockable(b)

    if (showFilters || showSchedules) {
      getKiboshDeviceFilterSetsAsync(blockableReference);
    }

    setReload(true);
  }

  const onEditClick = (accountBlockedSite: AccountBlockedSite) => {
    if (!(deviceReference || currentBlockableReference)) {
      setBlockableSelectionNeededPopupVisible(true); 
      return;
    }
    setCurrentAccountBlockedSite({ ...accountBlockedSite });
    setShowAccountBlockedSiteModal(true);
  };

  const onNewClick = () => {
    if (!(deviceReference || currentBlockableReference)) {
      setBlockableSelectionNeededPopupVisible(true); 
      return;
    }

    setCurrentAccountBlockedSite(new AccountBlockedSite());
    setShowAccountBlockedSiteModal(true);
  };

  const onDeleteClick = (accountBlockedSite: AccountBlockedSite) => {
    if (blockable?.filter(x => (x.id == deviceReference || x.id == currentBlockableReference) && !!x.groupId).length) {
      message.error(`Please edit from profile page`);

      return;
    }
    setCurrentAccountBlockedSite(accountBlockedSite);
    setIsDeleteConfirmationPopupOpen(true);
  };

  const handleDeleteRequest = async () => {
    setLoading(true);
    const response = await apiCall(DashApi.deleteClientDeviceFilter(kiboshDeviceReference || currentAccountKiboshDeviceReference!, currentBlockableReference || deviceReference!, currentAccountBlockedSite.id!));
    setLoading(false);

    if (response.success) {
      message.success(`Site unblocked successfully.`);
      setIsDeleteConfirmationPopupOpen(false);
      setReload(true);
    }
  };

  const onFilterCheckboxChange = async (item: AdminFilterSet, event: any) => {
    if (!(deviceReference || currentBlockableReference)) {
      setBlockableSelectionNeededPopupVisible(true); 
      return;
    }

    if (event.target?.checked) {
      const formData: accountBlockedSiteViewModel = {
        matchType: "set",
        deviceId: currentBlockableReference || deviceReference!,
        kiboshDeviceReference: kiboshDeviceReference || currentAccountKiboshDeviceReference,
        match: item.id!
      }

      let response: ApiResponse;

      setLoadingAccountSetBlockedSites(true);
      const data = convertToSnakecase(formData, [], ["kiboshDeviceReference", "deviceId"]);

      response = await apiCall(DashApi.insertClientDeviceFilter(formData.kiboshDeviceReference, formData.deviceId, data, abortController.signal));

      if (response.success) {
        message.success(`Site blocked successfully.`);
        getAccountSetBlockedSitesAsync();

      } else
        message.error(response.error?.value);

      setLoadingAccountSetBlockedSites(false);
    } else {
      setLoadingAccountSetBlockedSites(true);
      const formData = accountSetBlockedSites.find(x => x.match?.toLocaleLowerCase() === item.id?.toLocaleLowerCase());
      if (!formData) {
        message.error(`Item not found.`);
        setLoadingAccountSetBlockedSites(false);
        return;
      }
      const response = await apiCall(DashApi.deleteClientDeviceFilter(kiboshDeviceReference || currentAccountKiboshDeviceReference!, currentBlockableReference || deviceReference!, formData.id!));
      setLoadingAccountSetBlockedSites(false);

      if (response.success) {
        message.success(`Site unblocked successfully.`);
        setLoadingAccountSetBlockedSites(true);
        getAccountSetBlockedSitesAsync();
      }
    }
  }

  const onItemClick = (data: any) => {
    setExpandedItemId(expandedItemId === data.id ? "" : data.id);
  }

  const appBlocked = (appId:any) => {
    return (
      accountSetBlockedSites
        .filter((x: any) => !x.schedule)
        .map(x => x.match?.toLocaleLowerCase())
        .includes(appId)
    );
  }

  const appScheduled = (appId:any) => {
    return (
      accountSetBlockedSites
        .filter((x: any) => !!x.schedule)
        .map(x => x.match?.toLocaleLowerCase())
        .includes(appId)
    );
  }

  const getBlockStatusLabel = (isEnabled:boolean, id:string) => {
   
    if(id.includes("youtube_safe") || id.includes("youtube_strict")) {
      return ( isEnabled ? "Enforced" : "Disabled" ); 
    } else if(id.includes("apple_private")) {
      return ( isEnabled ? "Open" : "Disabled" ); 
    } else {
      return ( isEnabled ? "Blocked" : "Permitted" ); 

    }
  }


  const ListItem: React.FC<{ data: any }> = ({ data }) => (
    <>
      <div data-background="white" data-type="group" className={expandedItemId === data.id ? "show" : ""}>
        <Ripple>
          <group data-name="list_header"
          data-contain=""
          onClick={() => onItemClick(data)}
          >
            <group data-align="center" data-gap="5" data-wrap="no" data-cursor="pointer"
            data-space-horizontal="15"

            >
              <group data-ratio="1:1" data-length="40" data-height='40' data-shrink="no" data-index="1" data-radius="5" data-color="main">
              {data.icon && <group data-length="40" data-radius="40" data-contain="" data-height="40">
                  <picture data-object-fit="cover">
                    <img src={data.icon && data.icon.toString().startsWith('data:image') ? data.icon : `data:image/png;base64,${data.icon}`} alt="Filter Set Icon" />
                  </picture>
                </group>}
              </group>
              <group data-direction="column" data-contain="" data-space="5" data-index="1">
                <text data-weight="700" data-ellipsis="">{data.name}</text>
                <group data-wrap="no" data-contain="" data-name="marquee_cont">
                  <group data-marquee="" data-width="auto" data-max-length="auto">
                    <text data-weight={ appBlocked(data.id) ? "700" : "400" } data-color={ appBlocked(data.id) ? "main" : "light-gray" } data-text-size="10" >
                      { getBlockStatusLabel( appBlocked(data.id), data.id) }
                    </text>
                  </group>
                </group>
              </group>
              <group data-align="center" data-wrap="no" data-position="right" data-width="auto" data-index="1">
                { !!appScheduled(data.id) &&
                <group data-space="10">
                  <icon data-opacity="30">schedule</icon>
                </group>}
                <group data-space="10">
                  <icon data-opacity="30" data-interact-ready="rotate-180">keyboard_arrow_down</icon>
                </group>
              </group>
            </group>
          </group>
        </Ripple>

        <group data-name="list_cont">
          {expandedItemId === data.id &&
            <AccountApplicationDetails
              open={expandedItemId === data.id}
              minimal={true}
              account={account!}
              showTopBlockSection={false}
              deviceReference={currentBlockableReference || deviceReference! }
              accountKiboshDeviceReference={kiboshDeviceReference || currentAccountKiboshDeviceReference!}
              accountApplication={data}
              disabled={!!appBlocked(data.id)}
              onSave={() => getAccountSetBlockedSitesAsync()}
            />
          }
        </group>
      </div>
    </>
  );

  const renderApplicationFiltersets = () => (
    <>
      {(loadingAdminFilterSets || loadingAccountSetBlockedSites) && <Loader />}
      <group
      data-gap="none"
      data-direction="column"
      >
        {visibleAdminFilterSets.map((item) =>
          <group key={item.id} data-background="white" data-height="50">
            <label className={appBlocked(item.id) ? "checked" : "unchecked"} data-type="group" data-wrap="no" data-space="5" data-space-horizontal="15" data-align="center" data-cursor="pointer" data-interactive="" data-contain="" data-min-height="50">
              <group data-position="left" data-align="center" data-wrap="no" data-contain="" data-gap="10">
                {item.icon &&
                  <group data-length="40" data-radius="40" data-height='40' data-contain="">
                    <picture data-object-fit='cover'>
                      <img src={getBase64ImageUrl(item.icon)} alt="Filter Set Icon" />
                    </picture>
                  </group>
                }
                <group data-length="autofit" data-direction="column" data-contain="" >
                  <text data-weight="700" data-text-size="" data-ellipsis="">{item.name}</text>
                    <text data-weight={ appBlocked(item.id) ? "700" : "400" } data-color={ appBlocked(item.id) ? "main" : "light-gray" } data-text-size="10" >
                      { getBlockStatusLabel( appBlocked(item.id), ""+item.id) }
                    </text>
                </group>
              </group>
              <Checkbox
                data-position="right"
                checked={appBlocked(item.id)}
                onChange={(event: any) => onFilterCheckboxChange(item, event)}
                simple
              />
            </label>
            <separator horizontal="" data-margin="none"></separator>
          </group>
        )}</group>
    </>
  )

  const renderApplicationSchedules = () => (
    <>
      {(loadingAdminFilterSets || loadingAccountSetBlockedSites || loadingCategories) && <Loader />}
      <group>
        <ListView
          data-template="400"
          view={ListViewType.List}
          dataSource={visibleAdminFilterSets}
          keyField="id"
          listProps={{ "data-gap": "1" }}
          itemComponent={ListItem}
        />
      </group>
    </>
  )

  const renderCustomFiltersSection = () => (
    <view>
        <group
          data-align="center"
          data-gap="none"
          data-elevation="1"
          data-index="2"
          data-backdrop=""
          data-wrap="no"
          data-sticky="top"
          data-space="10"
        >
          <Button
            material
            icon="add"
            text="New"
            primary
            onClick={onNewClick}
          />
          <Button
            data-position="right"
            material
            icon="refresh"
            text="Refresh"
            outline
            onClick={() => setReload(true)}
          />
        </group>
        <view className="table_cont">
          <DataTable columns={columns}
            bordered={true}
            customPagination={true}
            customPageSize={25}
            reload={reload}
            dataPagination={true}
            getPageAsync={getPage}
            loading={loading}
            rowKey={record => `${record.accountBlockedSiteId}`}
            showTotal={false}
            size="small"
            totalRecords={totalRecords}
            onRow={(record) => ({
              onDoubleClick: () => onEditClick(record),
              style: { cursor: "pointer" },
            })}
          />
        </view>
      </view>
  );

  const renderApplicationsSection = () => (
    <group data-scroll="" style={ isMobile ? {} : { maxWidth: "850px"}} >
      <group data-border="inset" data-height="40" >
        <group
          data-gap='10'
          data-align='center'>
          <Options className="fit large square" bar={true}>
            <Checkbox checkboxType={CheckboxType.Button} label="Preset Apps" checked={"preset" === filterType} onClick={()=> setFilterType("preset")} />
            <Checkbox checkboxType={CheckboxType.Button} label="Custom" checked={"custom" === filterType} onClick={()=> setFilterType("custom")} />
          </Options>
        </group>
      </group>
      <group style={{ maxWidth: "550px", position: "absolute", left: "0px", top:"40px", display: filterType == "custom" ? "none" : "block" }} >
              <Select
                autoComplete=""
                label="Category"
                labelPosition="left"
                dataLength="435"
                style={{ minWidth: "435px", width: "435px" }}
                value={selectedCategory}
                onChange={ (v:any) => { setSelectedCategory(v)  }}
                options={ 
                  [ {value:"all", text:"All"}, ...categories?.map((item) => {return {value:"" + item.value, text: "" + item.valueCaption } ; }) ]
                }
              />
                <Input
                  type="text"
                  name="filter_search"
                  onChange={ (e:any) => { setFilterSetSearchText(e.target.value);  }  }
                  placeholder="Search"
                  style={{ minWidth: "435px", width: "435px", paddingLeft: "5px" }}

                />

          <TabStrip
            id="applications-switchable-tab"
            selectedIndex={selectedTabIndex}
            onChange={setSelectedTabIndex}>
            <Tab title='Filters'>
              {renderApplicationFiltersets()}
            </Tab>
            <Tab title='Schedules'>
              {renderApplicationSchedules()}
            </Tab>
          </TabStrip>
      </group>
      <group style={{  maxWidth: "550px", position: "absolute", left: "0px", top:"40px", display: filterType == "custom" ? "block" : "none"}} >
        { renderCustomFiltersSection() }
      </group>
    </group>
  )

  const getBlockableOption = (blockableItem:any) => {
    let isProfile = blockableItem["client_ids"] != null
    let isVpn = ( blockableItem["is_vpn"] == true )
    let bv =  blockableItem.id! 
    let bt  =  blockableItem.name!
    bt = bt == null ? "" + blockableItem.id! : bt
    bt = isProfile ? bt + " (Profile)" : bt
    bt = isVpn ? bt  + " (VPN)" : bt
    bt = (!isProfile) && (!isVpn) && blockableItem.ip ? bt + " (" + blockableItem.ip.replace("disconnected", "offline") + ")" : bt
    return { text: bt, value: "" + bv }
  };

  return (
    <>
      {loading &&
        <Loader />
      }
      <view data-direction="column">
        { ((!kiboshDeviceReference) || (!deviceReference) || (preselectedBlockable && preselectedBlockable.group_with_vpns)) &&
          <group data-space="15" data-gap="20" data-border="">
            {!kiboshDeviceReference && <>
              <Select
                autoComplete=""
                label="Kibosh Device"
                labelPosition="left"
                dataLength="320"
                onChange={onAccountKiboshDeviceChange}
                loading={loadingAccountKiboshDevices}
                allowSearch={true}
                options={
                  accountKiboshDevices?.map((item) => {
                    return {
                      text: `${item.kiboshDeviceName!} (${item.kiboshDeviceReference!})`,
                      value: item.kiboshDeviceReference!,
                    };
                  }) || []
                }
                button={
                  <Button
                    material
                    icon="refresh"
                    onClick={getAccountKiboshDevicesAsync}
                  />
                }
              />
            </>}
            { ((!deviceReference) || (preselectedBlockable && preselectedBlockable.group_with_vpns)) && <>
              <Select
                autoComplete=""
                label="Set Filters For"
                placeholder="Select Profile or Device"
                labelPosition="left"
                dataLength="435"
                style={{ minWidth: "435px", width: "435px" }}
                onChange={onBlockableChange}
                loading={loadingBlockable}
                value={currentBlockableReference}
                allowSearch={true}
                options={
                  [ {value:"lan", text:"All"}, ...blockable?.map((item) => {return getBlockableOption(item) ; }) ]
                }
                button={
                  <Button
                    material
                    icon="refresh"
                    onClick={getBlockableAsync}
                  />
                }
              />
            </>}
          </group>}

        <view data-direction="row">
          { renderApplicationsSection() }
        </view>
      </view>
      {!!showAccountBlockedSiteModal &&
        <AccountBlockedSiteModal open={showAccountBlockedSiteModal}
          account={account!}
          closeOnSave={true}
          deviceReference={currentBlockableReference || deviceReference!}
          kiboshDeviceReference={kiboshDeviceReference || currentAccountKiboshDeviceReference}
          accountBlockedSite={currentAccountBlockedSite}
          onClose={() => setShowAccountBlockedSiteModal(false)}
          onSave={(e) => onAccountBlockedSiteSave(e)} />
      }
      {isDeleteConfirmationPopupOpen && (
        <ConfirmationPopup
          showButton={false}
          positiveButtonText="Delete"
          positiveCallback={handleDeleteRequest}
          negativeCallback={() => {
            setIsDeleteConfirmationPopupOpen(false);
          }}
        />
      )}
      <InfoPopup
        title=" "
        message="Select device or profile you want to filter first"
        okCallback={ () => { setBlockableSelectionNeededPopupVisible(false); } }
        visible={ blockableSelectionNeededPopupVisible }
      />
    </>
  );
}
